import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";
import { Dropdown } from "../elements/EditDeviceModal";
import EmployeeImageCropModal from "../elements/EmployeeImageCropModal";
import SearchBar from "../elements/SearchBar";
import { validateEmployeeImageFile } from "../../utils/employeeImage";

const NO_GROUP_LABEL = "Bez grupe";
const EMPLOYEE_IME_MAX_LENGTH = 50;
const EMPLOYEE_PREZIME_MAX_LENGTH = 50;
const EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH = 4;
const GENERATED_INITIAL_PASSWORD_LENGTH = 8;

const generateInitialPassword = (length = GENERATED_INITIAL_PASSWORD_LENGTH) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const values = new Uint32Array(length);
    crypto.getRandomValues(values);
    return Array.from(values, (value) => chars[value % chars.length]).join('');
};

const normalizeGroupName = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return NO_GROUP_LABEL;
    return name;
};

const groupForApi = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return null;
    const trimmed = String(name).trim();
    if (!trimmed || trimmed.toLowerCase() === NO_GROUP_LABEL.toLowerCase()) return null;
    return trimmed;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getEmployeeFormValues = ({ imeRef, prezimeRef, emailRef, tagIdRef, groupRef, initialPasswordRef }) => ({
    ime: imeRef.current?.value?.trim() || "",
    prezime: prezimeRef.current?.value?.trim() || "",
    username: emailRef.current?.value?.trim() || "",
    tagid: tagIdRef.current?.value?.trim() || "",
    group: groupForApi(groupRef.current),
    ...(initialPasswordRef
        ? { initialPassword: initialPasswordRef.current?.value?.trim() || "" }
        : {}),
});

const getOriginalEmployeeValues = (employee) => ({
    ime: employee?.ime?.trim() || "",
    prezime: employee?.prezime?.trim() || "",
    username: employee?.username?.trim() || "",
    tagid: employee?.tagid?.trim() || "",
    group: groupForApi(employee?.group),
});

const hasEmployeeFormChanges = (formValues, originalValues) =>
    formValues.ime !== originalValues.ime
    || formValues.prezime !== originalValues.prezime
    || formValues.username !== originalValues.username
    || formValues.tagid !== originalValues.tagid
    || formValues.group !== originalValues.group
    || !!(formValues.initialPassword && formValues.initialPassword.length > 0);

const validateEmployeeFormValues = (formValues) => {
    if (!formValues.ime) {
        return { key: "form.errors.firstNameRequired" };
    }

    if (formValues.ime.length > EMPLOYEE_IME_MAX_LENGTH) {
        return { key: "form.errors.firstNameMax", params: { max: EMPLOYEE_IME_MAX_LENGTH } };
    }

    if (!formValues.prezime) {
        return { key: "form.errors.lastNameRequired" };
    }

    if (formValues.prezime.length > EMPLOYEE_PREZIME_MAX_LENGTH) {
        return { key: "form.errors.lastNameMax", params: { max: EMPLOYEE_PREZIME_MAX_LENGTH } };
    }

    if (!isValidEmail(formValues.username)) {
        return { key: "form.errors.emailInvalid" };
    }

    return null;
};

const validateEmployeeCreateFormValues = (formValues) => {
    const baseError = validateEmployeeFormValues(formValues);
    if (baseError) {
        return baseError;
    }

    if (!formValues.initialPassword) {
        return { key: "form.errors.passwordRequired" };
    }

    if (formValues.initialPassword.length < EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH) {
        return { key: "form.errors.passwordMin", params: { min: EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH } };
    }

    return null;
};

const validateEmployeeResetPinFormValues = (formValues) => {
    if (!formValues.initialPassword) {
        return null;
    }

    if (formValues.initialPassword.length < EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH) {
        return { key: "form.errors.passwordMin", params: { min: EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH } };
    }

    return null;
};

const resolveSettingsError = (error, t) => {
    if (!error) return "";
    if (typeof error === "string") {
        if (error.includes(".")) {
            return t(error);
        }
        return error;
    }
    return t(error.key, error.params ?? {});
};

const normalizeSearchText = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

const getFieldMatchScore = (fieldValue, query) => {
    const field = normalizeSearchText(fieldValue);
    if (!field || !query) return 0;

    if (field === query) return 100;
    if (field.startsWith(query)) return 80 + Math.min(15, (query.length / field.length) * 15);
    if (field.includes(query)) return 50 + Math.min(20, (query.length / field.length) * 20);

    const tokens = field.split(/\s+/);
    let bestTokenScore = 0;
    for (const token of tokens) {
        if (token === query) bestTokenScore = Math.max(bestTokenScore, 90);
        else if (token.startsWith(query)) bestTokenScore = Math.max(bestTokenScore, 70);
        else if (token.includes(query)) bestTokenScore = Math.max(bestTokenScore, 40);
    }

    return bestTokenScore;
};

const getEmployeeSearchScore = (employee, rawQuery) => {
    const query = normalizeSearchText(rawQuery);
    if (!query) return 0;

    const fullName = `${employee?.ime || ""} ${employee?.prezime || ""}`.trim();
    const fields = [
        fullName,
        employee?.ime,
        employee?.prezime,
        employee?.username,
        employee?.tagid,
        employee?.group || NO_GROUP_LABEL,
    ];

    let best = 0;
    let matchedFields = 0;
    for (const field of fields) {
        const score = getFieldMatchScore(field, query);
        if (score > 0) matchedFields += 1;
        best = Math.max(best, score);
    }

    return best + matchedFields * 2;
};

const fieldMatchesSearch = (fieldValue, rawQuery) => {
    const query = normalizeSearchText(rawQuery);
    if (!query) return false;
    return getFieldMatchScore(fieldValue, query) > 0;
};

const ROW_FADE_OUT_MS = 120;
const ROW_STAGGER_WINDOW_MS = 220;

const getRowFadeInDelayMs = (index, total) => {
    if (total <= 1 || index <= 0) return 0;
    const progress = index / (total - 1);
    // ease-out: razmaci se sabijaju ka kraju → ubrzava se
    const eased = 1 - Math.pow(1 - progress, 2.6);
    return Math.round(eased * ROW_STAGGER_WINDOW_MS);
};

const SettingsView = () => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const smallScreen = useLayoutStore((state) => state.smallScreen);
    const employeesData = useAppStore((state) => state.employeesData);
    const loading = useAppStore((state) => state.loading);
    const employeeDeleteSuccessToast = useAppStore((state) => state.employeeDeleteSuccessToast);
    const employeeUpdateSuccessToast = useAppStore((state) => state.employeeUpdateSuccessToast);
    const employeeCreateSuccessToast = useAppStore((state) => state.employeeCreateSuccessToast);

    const [hoveredRowId, setHoveredRowId] = useState(null);
    const [activeRowId, setActiveRowId] = useState(null);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [displayedEmployees, setDisplayedEmployees] = useState([]);
    const [rowsOpaque, setRowsOpaque] = useState(true);
    const prevSearchRef = useRef(null);
    const isEmployeeFormOpen = useAppStore((state) => state.isEmployeeFormOpen);
    const setIsEmployeeFormOpen = useAppStore((state) => state.setIsEmployeeFormOpen);
    const [employeeFormMode, setEmployeeFormMode] = useState("create");
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const editingEmployee = useMemo(() => {
        if (!isEmployeeFormOpen || employeeFormMode !== "edit" || !selectedEmployee?.user_id) {
            return selectedEmployee;
        }

        const userKey = selectedEmployee.user_id;
        const fromStore = employeesData?.[userKey]
            ?? Object.values(employeesData || {}).find(
                (item) => String(item.user_id) === String(userKey),
            );

        return fromStore || selectedEmployee;
    }, [isEmployeeFormOpen, employeeFormMode, selectedEmployee, employeesData]);

    const activeThemeClass = isDarkTheme ? "dark-color" : "white-color";
    const sortedEmployees = useMemo(() => {
        if (!employeesData) return [];

        const list = Object.values(employeesData);
        const query = employeeSearch.trim();

        if (!query) {
            return list.sort((a, b) => {
                const aName = `${a?.ime || ""} ${a?.prezime || ""}`.trim();
                const bName = `${b?.ime || ""} ${b?.prezime || ""}`.trim();
                return aName.localeCompare(bName, "sr", { sensitivity: "base" });
            });
        }

        return list
            .map((employee) => ({
                employee,
                score: getEmployeeSearchScore(employee, query),
            }))
            .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score;
                const aName = `${a.employee?.ime || ""} ${a.employee?.prezime || ""}`.trim();
                const bName = `${b.employee?.ime || ""} ${b.employee?.prezime || ""}`.trim();
                return aName.localeCompare(bName, "sr", { sensitivity: "base" });
            })
            .map(({ employee }) => employee);
    }, [employeesData, employeeSearch]);

    useEffect(() => {
        if (prevSearchRef.current === null) {
            prevSearchRef.current = employeeSearch;
            setDisplayedEmployees(sortedEmployees);
            setRowsOpaque(true);
            return;
        }

        if (prevSearchRef.current === employeeSearch) {
            setDisplayedEmployees(sortedEmployees);
            return;
        }

        let cancelled = false;
        setRowsOpaque(false);

        const timeoutId = setTimeout(() => {
            if (cancelled) return;
            prevSearchRef.current = employeeSearch;
            setDisplayedEmployees(sortedEmployees);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!cancelled) setRowsOpaque(true);
                });
            });
        }, ROW_FADE_OUT_MS);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [employeeSearch, sortedEmployees]);

    const handleEditClick = (employee) => {
        setActiveRowId(employee.user_id);
        setSelectedEmployee(employee);
        setEmployeeFormMode("edit");
        setIsEmployeeFormOpen(true);
    };

    const handleCreateClick = () => {
        setSelectedEmployee(null);
        setEmployeeFormMode("create");
        setIsEmployeeFormOpen(true);
    };

    const handleCloseEmployeeForm = () => {
        setIsEmployeeFormOpen(false);
    };

    const getRowClassName = (userId) => {
        const classes = [];
        if (hoveredRowId === userId) classes.push("st-row-hover");
        if (activeRowId === userId) classes.push("st-row-active");
        return classes.join(" ");
    };

    const getRowHandlers = (employee) => ({
        onMouseEnter: () => setHoveredRowId(employee.user_id),
        onMouseLeave: () => setHoveredRowId(null),
        onClick: () => handleEditClick(employee),
    });

    const getCellAnimStyle = (index) => {
        const delayMs = rowsOpaque
            ? getRowFadeInDelayMs(index, displayedEmployees.length)
            : 0;
        return {
            opacity: rowsOpaque ? 1 : 0,
            transition: `opacity 0.12s ease ${delayMs}ms`,
        };
    };

    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
    const tableClass = `kv-table ${isDarkTheme ? "dark-table" : ""}`;

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>{t("title")}</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>{t("description")}</div>
                </div>
            </div>
            <div className="settings-content-container">
                <div className="settings-section-1-container">
                    <div className="st-employee-div">
                        <div className="st-header-row">
                            <h2 className={`st-employee-title ${activeThemeClass}`}>{t("employees.title")}</h2>
                            <div className="st-header-actions">
                                <button
                                    type="button"
                                    className={`cv-new-absence-button st-add-employee-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                    onClick={handleCreateClick}
                                >
                                    {t("employees.add")}
                                </button>
                                <SearchBar
                                    onSearchChange={setEmployeeSearch}
                                    expandedWidth={smallScreen ? 224 : 244}
                                />
                            </div>
                        </div>
                        <div className="kv-table-container st-table-shell">
                            <div className="st-table-scroll">
                                <table className={`${tableClass} st-employee-table-main`}>
                                    <thead>
                                        <tr>
                                            <th className="st-row-num-col">{t("employees.columns.number")}</th>
                                            <th className="st-avatar-col"> </th>
                                            <th>{t("employees.columns.firstName")}</th>
                                            <th>{t("employees.columns.lastName")}</th>
                                            <th className="st-email-col">{t("employees.columns.email")}</th>
                                            <th>{t("employees.columns.tagId")}</th>
                                            <th>{t("employees.columns.group")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && displayedEmployees.length === 0 && (
                                            <tr>
                                                <td colSpan={7} className="st-empty-row">
                                                    {t("employees.empty")}
                                                </td>
                                            </tr>
                                        )}
                                        {displayedEmployees.map((employee, index) => {
                                            const cellAnimStyle = getCellAnimStyle(index);
                                            const matchClass = (value) =>
                                                fieldMatchesSearch(value, employeeSearch) ? "st-cell-match" : "";
                                            return (
                                            <tr
                                                key={employee.user_id}
                                                className={getRowClassName(employee.user_id)}
                                                {...getRowHandlers(employee)}
                                            >
                                                <td className="st-row-num-col" style={cellAnimStyle}>{index + 1}</td>
                                                <td className="st-avatar-col" style={cellAnimStyle}>
                                                    <img
                                                        src={`${baseImgUrl}/employees/${employee.image}`}
                                                        alt=""
                                                        className={`st-employee-avatar ${isDarkTheme ? "dark-border" : ""}`}
                                                        crossOrigin="use-credentials"
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = "/views/login.svg";
                                                        }}
                                                    />
                                                </td>
                                                <td className={matchClass(employee.ime)} style={cellAnimStyle}>{employee.ime || "-"}</td>
                                                <td className={matchClass(employee.prezime)} style={cellAnimStyle}>{employee.prezime || "-"}</td>
                                                <td className={`st-email-col ${matchClass(employee.username)}`.trim()} style={cellAnimStyle}>{employee.username || "-"}</td>
                                                <td className={matchClass(employee.tagid)} style={cellAnimStyle}>{employee.tagid || "-"}</td>
                                                <td className={matchClass(employee.group || NO_GROUP_LABEL)} style={cellAnimStyle}>{employee.group || t("employees.noGroup")}</td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="st-table-actions">
                                <table className={`${tableClass} st-actions-table`}>
                                    <thead>
                                        <tr>
                                            <th className="asv-emp-day-logs-table-col-action"> </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && displayedEmployees.length === 0 && (
                                            <tr>
                                                <td className="asv-emp-day-logs-table-col-action" />
                                            </tr>
                                        )}
                                        {displayedEmployees.map((employee, index) => (
                                            <tr
                                                key={employee.user_id}
                                                className={getRowClassName(employee.user_id)}
                                                {...getRowHandlers(employee)}
                                            >
                                                <td className="asv-emp-day-logs-table-col-action" style={getCellAnimStyle(index)}>
                                                    <button
                                                        type="button"
                                                        className="asv-emp-day-log-edit-btn"
                                                        aria-label={t("employees.editAria", { name: `${employee.ime || ""} ${employee.prezime || ""}`.trim() })}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(employee);
                                                        }}
                                                    >
                                                        <img
                                                            className={`dv-card-edit-tool-btn ${isDarkTheme ? "dark-filter" : ""}`}
                                                            src="/cards/Edit.svg"
                                                            alt=""
                                                        />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {employeeDeleteSuccessToast && <EmployeeDeleteSuccessToast key="employee-delete-toast" />}
                {employeeUpdateSuccessToast && <EmployeeUpdateSuccessToast key="employee-update-toast" />}
                {employeeCreateSuccessToast && <EmployeeCreateSuccessToast key="employee-create-toast" />}
                {isEmployeeFormOpen && (
                    <EmployeeFormModal
                        key={editingEmployee?.user_id ?? "create"}
                        mode={employeeFormMode}
                        employee={editingEmployee}
                        onClose={handleCloseEmployeeForm}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SettingsView;

const EmployeeDeleteSuccessToast = () => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeDeleteSuccessToast = useAppStore((state) => state.setEmployeeDeleteSuccessToast);

    const dismiss = () => setEmployeeDeleteSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                {t("toast.deleted")}
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeUpdateSuccessToast = () => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeUpdateSuccessToast = useAppStore((state) => state.setEmployeeUpdateSuccessToast);

    const dismiss = () => setEmployeeUpdateSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                {t("toast.updated")}
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeCreateSuccessToast = () => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeCreateSuccessToast = useAppStore((state) => state.setEmployeeCreateSuccessToast);

    const dismiss = () => setEmployeeCreateSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                {t("toast.created")}
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeFormModal = ({ mode, employee, onClose }) => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const deviceGroupsState = useAppStore((state) => state.deviceGroupsState);
    const groupsState = useAppStore((state) => state.groupsState);
    const deleteEmployee = useAppStore((state) => state.deleteEmployee);
    const updateEmployee = useAppStore((state) => state.updateEmployee);
    const createEmployee = useAppStore((state) => state.createEmployee);
    const scannedUnknownTagId = useAppStore((state) => state.scannedUnknownTagId);
    const scannedUnknownTagTick = useAppStore((state) => state.scannedUnknownTagTick);

    const modalContainerRef = useRef(null);
    const imageInputRef = useRef(null);
    const imeRef = useRef(null);
    const prezimeRef = useRef(null);
    const emailRef = useRef(null);
    const tagIdRef = useRef(null);
    const initialPasswordRef = useRef(null);
    const groupRef = useRef(normalizeGroupName(employee?.group));

    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [addingNewItemId, setAddingNewItemId] = useState(null);
    const [formError, setFormError] = useState(null);
    const [tagIdFlash, setTagIdFlash] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [pendingImagePreview, setPendingImagePreview] = useState(null);
    const [pendingImageBlob, setPendingImageBlob] = useState(null);

    const isEditMode = mode === "edit";
    const showPendingAccountSetupPin = isEditMode && employee?.pendingAccountSetup === true;
    const TAG_ID_CHAR_MS = 38;
    const TAG_ID_FLASH_MS = 520;
    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;

    const groupOptions = useMemo(() => {
        const names = new Set([NO_GROUP_LABEL]);

        Object.keys(deviceGroupsState || {}).forEach((name) => {
            names.add(normalizeGroupName(name));
        });
        Object.keys(groupsState || {}).forEach((name) => {
            names.add(normalizeGroupName(name));
        });

        if (employee?.group) names.add(normalizeGroupName(employee.group));
        if (groupRef.current) names.add(normalizeGroupName(groupRef.current));

        return Array.from(names)
            .filter(Boolean)
            .sort((a, b) => {
                if (a === NO_GROUP_LABEL) return 1;
                if (b === NO_GROUP_LABEL) return -1;
                return a.localeCompare(b, "sr", { sensitivity: "base" });
            });
    }, [deviceGroupsState, groupsState, employee?.group]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isDeleteConfirmOpen || isCropModalOpen) return;
            if (modalContainerRef.current?.contains(e.target)) return;
            onClose();
        };

        document.addEventListener("mousedown", handleClickOutside, true);
        return () => document.removeEventListener("mousedown", handleClickOutside, true);
    }, [onClose, isDeleteConfirmOpen, isCropModalOpen]);

    useEffect(() => () => {
        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    }, [cropImageSrc, pendingImagePreview]);

    useEffect(() => {
        if (!scannedUnknownTagTick || !scannedUnknownTagId) return undefined;

        const tag = String(scannedUnknownTagId).trim();
        if (!tag || !tagIdRef.current) return undefined;

        let index = 0;
        let flashTimer;
        tagIdRef.current.value = "";
        setTagIdFlash(false);
        setFormError(null);

        const interval = window.setInterval(() => {
            index += 1;
            if (tagIdRef.current) {
                tagIdRef.current.value = tag.slice(0, index);
            }

            if (index >= tag.length) {
                window.clearInterval(interval);
                setTagIdFlash(true);
                flashTimer = window.setTimeout(() => setTagIdFlash(false), TAG_ID_FLASH_MS);
            }
        }, TAG_ID_CHAR_MS);

        return () => {
            window.clearInterval(interval);
            if (flashTimer) window.clearTimeout(flashTimer);
        };
    }, [scannedUnknownTagTick, scannedUnknownTagId]);

    const handleGroupChange = (value) => {
        groupRef.current = normalizeGroupName(value);
        clearFormError();
    };

    const clearFormError = () => {
        if (formError) {
            setFormError(null);
        }
    };

    const handleGenerateInitialPassword = () => {
        if (!initialPasswordRef.current) return;
        initialPasswordRef.current.value = generateInitialPassword();
        clearFormError();
    };

    const handleAvatarClick = () => {
        imageInputRef.current?.click();
    };

    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";

        if (!file) return;

        const validationError = await validateEmployeeImageFile(file);
        if (validationError) {
            setFormError(validationError);
            return;
        }

        clearFormError();

        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        const nextCropSrc = URL.createObjectURL(file);
        setCropImageSrc(nextCropSrc);
        setIsCropModalOpen(true);
    };

    const handleCropCancel = () => {
        setIsCropModalOpen(false);
        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
            setCropImageSrc(null);
        }
    };

    const handleCropConfirm = (blob) => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);

        const previewUrl = URL.createObjectURL(blob);
        setPendingImageBlob(blob);
        setPendingImagePreview(previewUrl);
        setIsCropModalOpen(false);

        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
            setCropImageSrc(null);
        }
    };

    const handleConfirm = async () => {
        if (isSubmitting) return;

        const formValues = getEmployeeFormValues({
            imeRef,
            prezimeRef,
            emailRef,
            tagIdRef,
            groupRef,
            ...((!isEditMode || showPendingAccountSetupPin) ? { initialPasswordRef } : {}),
        });

        if (isEditMode) {
            const originalValues = getOriginalEmployeeValues(employee);

            if (!hasEmployeeFormChanges(formValues, originalValues) && !pendingImageBlob) {
                setFormError({ key: "form.errors.noChanges" });
                return;
            }

            const validationError = validateEmployeeFormValues(formValues)
                || validateEmployeeResetPinFormValues(formValues);
            if (validationError) {
                setFormError(validationError);
                return;
            }

            setFormError(null);
            setIsSubmitting(true);

            try {
                const success = await updateEmployee({
                    user_id: employee.user_id,
                    ...formValues,
                    ...(pendingImageBlob ? { image: pendingImageBlob } : {}),
                    ...(formValues.initialPassword ? { initialPassword: formValues.initialPassword } : {}),
                });

                if (success === true) {
                    onClose();
                    return;
                }

                setFormError(typeof success === "string" ? success : { key: "form.errors.saveFailed" });
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        const validationError = validateEmployeeCreateFormValues(formValues);
        if (validationError) {
            setFormError(validationError);
            return;
        }

        setFormError(null);
        setIsSubmitting(true);

        try {
            const success = await createEmployee({
                ...formValues,
                ...(pendingImageBlob ? { image: pendingImageBlob } : {}),
            });

            if (success === true) {
                onClose();
                return;
            }

            setFormError(typeof success === "string" ? success : { key: "form.errors.createFailed" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = () => {
        if (!employee?.user_id) return;
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!employee?.user_id) return;

        const success = await deleteEmployee(employee.user_id);
        setIsDeleteConfirmOpen(false);

        if (success) {
            onClose();
        }
    };

    const title = isEditMode ? t("form.editTitle") : t("form.createTitle");
    const subtitle = isEditMode
        ? `${employee?.ime || ""} ${employee?.prezime || ""}`.trim()
        : t("form.createSubtitle");

    const hasServerImage = Boolean(employee?.image) && !pendingImagePreview;
    const hasDisplayImage = Boolean(pendingImagePreview) || hasServerImage;
    const displayImageSrc = pendingImagePreview
        ? pendingImagePreview
        : hasServerImage
            ? `${baseImgUrl}/employees/${employee.image}`
            : null;
    const avatarActionLabel = hasDisplayImage ? t("form.editImage") : t("form.addImage");

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container st-employee-form-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, delay: 0.14 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: "none" }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`dv-edit-modal st-employee-form-modal ${isDarkTheme ? "dark-dv-edit-modal" : "white-dv-edit-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: "auto" }}
                >
                    <button
                        type="button"
                        className={`dv-edit-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={onClose}
                        aria-label={t("form.closeAria")}
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>

                    <h3>
                        <img src="/sidebar/Podesavanja.svg" alt="" className={isDarkTheme ? "dark-filter" : ""} />
                        {title}
                    </h3>

                    <div className="st-employee-form-avatar-wrap">
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="st-employee-form-avatar-input"
                            onChange={handleImageFileChange}
                        />
                        <button
                            type="button"
                            className="st-employee-form-avatar-btn"
                            aria-label={avatarActionLabel}
                            onClick={handleAvatarClick}
                        >
                            {displayImageSrc ? (
                                <img
                                    src={displayImageSrc}
                                    alt=""
                                    className="st-employee-form-avatar"
                                    crossOrigin={pendingImagePreview ? undefined : "use-credentials"}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = "/views/login.svg";
                                    }}
                                />
                            ) : (
                                <div className="st-employee-form-avatar st-employee-form-avatar--placeholder">
                                    <img
                                        src="/views/login.svg"
                                        alt=""
                                        className="st-employee-form-avatar-placeholder-icon"
                                    />
                                </div>
                            )}
                            <span className="st-employee-form-avatar-overlay">
                                <img
                                    className={`st-employee-form-avatar-edit-icon ${isDarkTheme ? "dark-filter" : ""}`}
                                    src="/cards/Edit.svg"
                                    alt=""
                                />
                                <span className="st-employee-form-avatar-overlay-label">{avatarActionLabel}</span>
                            </span>
                        </button>
                    </div>

                    <div className="dv-edit-settings-container st-employee-form-fields">
                        <div className="dv-edit-field">
                            <label>{t("form.labels.firstName")}</label>
                            <input
                                ref={imeRef}
                                type="text"
                                className="st-employee-form-input"
                                defaultValue={employee?.ime || ""}
                                placeholder={t("form.placeholders.firstName")}
                                maxLength={EMPLOYEE_IME_MAX_LENGTH}
                                onChange={clearFormError}
                            />
                        </div>

                        <div className="dv-edit-field">
                            <label>{t("form.labels.lastName")}</label>
                            <input
                                ref={prezimeRef}
                                type="text"
                                className="st-employee-form-input"
                                defaultValue={employee?.prezime || ""}
                                placeholder={t("form.placeholders.lastName")}
                                maxLength={EMPLOYEE_PREZIME_MAX_LENGTH}
                                onChange={clearFormError}
                            />
                        </div>

                        <div className="dv-edit-field">
                            <label>{t("form.labels.email")}</label>
                            <input
                                ref={emailRef}
                                type="email"
                                className="st-employee-form-input"
                                defaultValue={employee?.username || ""}
                                placeholder={t("form.placeholders.email")}
                                onChange={clearFormError}
                            />
                        </div>

                        {!isEditMode && (
                            <div className="dv-edit-field st-tagid-field">
                                <label>{t("form.labels.initialPassword")}</label>
                                <input
                                    ref={initialPasswordRef}
                                    type="password"
                                    className="st-employee-form-input"
                                    placeholder={t("form.placeholders.initialPassword")}
                                    autoComplete="new-password"
                                    onChange={clearFormError}
                                />
                                <div className="st-employee-form-tagid-hint">
                                    {t("form.hints.initialPassword")}
                                </div>
                            </div>
                        )}

                        {showPendingAccountSetupPin && (
                            <div className="dv-edit-field st-tagid-field">
                                <label>{t("form.labels.resetPin")}</label>
                                <div className="st-employee-pin-row">
                                    <input
                                        ref={initialPasswordRef}
                                        type="text"
                                        className="st-employee-form-input st-employee-pin-input"
                                        placeholder={t("form.placeholders.resetPin")}
                                        autoComplete="off"
                                        onChange={clearFormError}
                                    />
                                    <button
                                        type="button"
                                        className={`st-employee-generate-pin-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={handleGenerateInitialPassword}
                                    >
                                        {t("form.generatePin")}
                                    </button>
                                </div>
                                <div className="st-employee-form-tagid-hint">
                                    {t("form.hints.resetPin")}
                                </div>
                            </div>
                        )}

                        <div className="dv-edit-field st-tagid-field">
                            <label>{t("form.labels.tagId")}</label>
                            <input
                                ref={tagIdRef}
                                type="text"
                                readOnly
                                className={`st-employee-form-input st-employee-form-input--tag-readonly${tagIdFlash ? " st-employee-form-input--tag-flash" : ""}`}
                                defaultValue={employee?.tagid || ""}
                                placeholder={t("form.placeholders.tagId")}
                                onBeforeInput={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onDrop={(e) => e.preventDefault()}
                            />
                            <div className="st-employee-form-tagid-hint">
                                {t("form.hints.tagId")}
                            </div>
                        </div>

                        <div className="dv-edit-field">
                            <label>{t("form.labels.group")}</label>
                            <Dropdown
                                dropdownId="employee-group"
                                openDropdownId={openDropdownId}
                                setOpenDropdownId={setOpenDropdownId}
                                addingNewItemId={addingNewItemId}
                                setAddingNewItemId={setAddingNewItemId}
                                valueRef={groupRef}
                                onChange={handleGroupChange}
                                dropdownItems={groupOptions}
                                label={t("form.labels.group")}
                            />
                        </div>

                        {isEditMode && (
                            <button
                                type="button"
                                className="st-employee-delete-btn"
                                onClick={handleDeleteClick}
                                aria-label={t("form.deleteUser")}
                            >
                                <img
                                    src="/cards/kanta.svg"
                                    alt=""
                                    className={isDarkTheme ? "dark-filter" : ""}
                                />
                                <span>{t("form.deleteUser")}</span>
                            </button>
                        )}

                        <div className="st-employee-form-footer">
                            <div className="st-employee-form-actions">
                                <AnimatePresence>
                                    {formError && (
                                        <motion.div
                                            key="employee-form-error"
                                            className="st-employee-form-error"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            exit={{ scale: 0 }}
                                            transition={{ duration: 0.19, ease: "easeInOut" }}
                                        >
                                            {resolveSettingsError(formError, t)}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <button
                                    type="button"
                                    className={`dv-edit-save-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                    onClick={handleConfirm}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t("form.saving") : t("form.confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: "auto" }}
            />

            <AnimatePresence>
                {isCropModalOpen && cropImageSrc && (
                    <EmployeeImageCropModal
                        key="employee-image-crop"
                        imageSrc={cropImageSrc}
                        onConfirm={handleCropConfirm}
                        onClose={handleCropCancel}
                    />
                )}
                {isDeleteConfirmOpen && (
                    <motion.div
                        className="st-delete-confirm-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                    >
                        <motion.div
                            className={`st-delete-confirm-backdrop ${isDarkTheme ? "st-delete-confirm-backdrop--dark" : ""}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            onClick={() => setIsDeleteConfirmOpen(false)}
                            aria-hidden="true"
                        />
                        <motion.div
                            className="st-delete-confirm-modal-wrap"
                            initial={{ opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                className={`dv-delete-confirm-modal ${isDarkTheme ? "dark-dv-delete-confirm-modal" : "white-dv-delete-confirm-modal"}`}
                            >
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={() => setIsDeleteConfirmOpen(false)}
                                    aria-label={t("deleteConfirm.closeAria")}
                                    style={{ position: "absolute", top: "19px", right: "19px" }}
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                                <h3>{t("deleteConfirm.title")}</h3>
                                <p>
                                    {t("deleteConfirm.messageBefore")}
                                    <b>{t("deleteConfirm.deleteVerb")}</b>
                                    {t("deleteConfirm.messageAfter")}
                                    <b>{`${employee?.ime || ""} ${employee?.prezime || ""}`.trim()}</b>
                                    {t("deleteConfirm.messageEnd")}
                                </p>
                                <div className="dv-delete-confirm-modal-buttons">
                                    <button
                                        type="button"
                                        className={`dv-delete-confirm-modal-button cancel-btn ${isDarkTheme ? "dark-btn-close" : ""}`}
                                        onClick={() => setIsDeleteConfirmOpen(false)}
                                    >
                                        {t("deleteConfirm.cancel")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`dv-delete-confirm-modal-button confirm-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={handleDeleteConfirm}
                                    >
                                        {t("deleteConfirm.confirm")}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );

    return createPortal(modalContent, document.body);
};
